import { MediaWrapperContainerTypeEnum } from '@/utils/aspectRatioUtils';

import { MediaWrapper, type MediaWrapperProps } from '@/cms/MediaWrapper';
import { Container } from '@/components/Container';
import { NewSlider } from '@/components/NewSlider';
import type { ContentfulContentGroupFragment } from '@/graphql/contentful/api';
import type { ContentfulData, ContentType } from '@/integrations/contentful';
import { generateSectionKey } from '@/integrations/contentful';
import { generateDataTestId } from '@/lib/tests';
import type { WithHTMLProp } from '@/lib/types';

import type { CardProps } from '../Card';
import { Card } from '../Card';
import { CMSSection } from '../CMSSection';
import type { WallItemProps } from '../FourColumnScroll';
import type { ProductCardProps } from '../ProductCard';

import * as S from './ThreeColumnScroll.styles';

export interface NewsSliderProps extends MediaWrapperProps {
  internalName?: string;
  preload?: boolean;
}

type ThreeColumnScrollProps = ContentfulContentGroupFragment &
  WithHTMLProp<'className'>;

export const ThreeColumnScroll = ({
  itemsCollection,
}: ThreeColumnScrollProps) => {
  return (
    <CMSSection
      {...generateDataTestId('ThreeColumnScroll', 'section')}
      data-test-id='ThreeColumnScroll-section'
    >
      <Container disableGutters maxWidth='lg'>
        <NewSlider disableGutters variantColor='dark'>
          <NewSlider.Slider
            separator={false}
            slidesPerView={itemsCollection?.items.length}
            spaceBetween={20}
          >
            {itemsCollection?.items.filter(Boolean).map((item, i, array) => {
              const props = (item ? { ...item } : null) as ItemData;
              if (props) {
                props.preload = true;
              }
              const renderedItem = renderCollectionItem(props, i, array.length);
              return renderedItem ? (
                renderCollectionItem(item as ItemData, i, array.length)
              ) : (
                <></>
              );
            })}
          </NewSlider.Slider>
        </NewSlider>
      </Container>
    </CMSSection>
  );
};

type ItemData = CardProps | MediaWrapperProps | ProductCardProps | null;

export const renderCollectionItem = (
  data: ItemData,
  i: number,
  arrayLength: number,
) => {
  let props = null;
  const typename = data?.__typename as ContentType;
  const sysId = data?.sys?.id;
  switch (typename) {
    case 'Card':
      props = data as WallItemProps;
      return (
        <S.ImageTeaserCol
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`ThreeColumnScroll-ImageTeaserCol-${i}`}
          md={12 / arrayLength}
        >
          <Card
            key={sysId}
            {...props}
            cardGroupType={MediaWrapperContainerTypeEnum.ThreeColumnScroll}
          />
        </S.ImageTeaserCol>
      );
    case 'ImageWithFocalPoint':
      props = data as MediaWrapperProps;
      return (
        <S.ImageTeaserCol
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`ThreeColumnScroll-ImageTeaserCol-${i}`}
          md={12 / arrayLength}
        >
          <MediaWrapper
            {...props}
            key={sysId}
            mediaWrapperType='ThreeColumnScroll'
          />
        </S.ImageTeaserCol>
      );
    default:
      return null;
  }
};

ThreeColumnScroll.displayName = 'ThreeColumnScroll';
